import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { petInfoService } from '../../../../services/petInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PetInfoInput');

type PetInfoInputScreenRouteProp = RouteProp<RootStackParamList, 'PetInfoInput'>;

export const PetInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<PetInfoInputScreenRouteProp>();

  const { petData, isEditing } = route.params || {};

  // Helper function to format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;

    // Limit to 10 digits
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const [petName, setPetName] = useState(petData?.petName || '');
  const [petType, setPetType] = useState(petData?.petType || '');
  const [breed, setBreed] = useState(petData?.breed || '');
  const [age, setAge] = useState(petData?.age?.toString() || '');
  const [weight, setWeight] = useState(petData?.weight?.toString() || '');
  const [color, setColor] = useState(petData?.color || '');
  const [gender, setGender] = useState(petData?.gender || '');
  const [microchipNumber, setMicrochipNumber] = useState(petData?.microchipNumber || '');
  const [registrationNumber, setRegistrationNumber] = useState(petData?.registrationNumber || '');
  const [veterinarianName, setVeterinarianName] = useState(petData?.veterinarianName || '');
  const [veterinarianPhone, setVeterinarianPhone] = useState(petData?.veterinarianPhone || '');
  const [veterinarianAddress, setVeterinarianAddress] = useState(petData?.veterinarianAddress || '');
  const [emergencyVetName, setEmergencyVetName] = useState(petData?.emergencyVetName || '');
  const [emergencyVetPhone, setEmergencyVetPhone] = useState(petData?.emergencyVetPhone || '');
  const [medicalConditions, setMedicalConditions] = useState(petData?.medicalConditions || '');
  const [medications, setMedications] = useState(petData?.medications || '');
  const [allergies, setAllergies] = useState(petData?.allergies || '');
  const [feedingInstructions, setFeedingInstructions] = useState(petData?.feedingInstructions || '');
  const [specialNeeds, setSpecialNeeds] = useState(petData?.specialNeeds || '');
  const [behavioralNotes, setBehavioralNotes] = useState(petData?.behavioralNotes || '');
  const [emergencyContactName, setEmergencyContactName] = useState(petData?.emergencyContactName || '');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState(petData?.emergencyContactPhone || '');
  const [emergencyContactRelationship, setEmergencyContactRelationship] = useState(petData?.emergencyContactRelationship || '');

  const [saving, setSaving] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedPetId, setSavedPetId] = useState<string | undefined>();

  const handleSave = async () => {
    // Validation - require at least pet name
    if (!petName.trim()) {
      Alert.alert('Error', 'Please enter at least the pet\'s name');
      return;
    }

    try {
      setSaving(true);

      const petUpdateData = {
        taskId: 5, // Pet task ID
        petName: petName.trim(),
        petType: petType.trim(),
        breed: breed.trim(),
        age: age ? parseInt(age, 10) : undefined,
        weight: weight ? parseInt(weight, 10) : undefined,
        color: color.trim(),
        gender: gender.trim(),
        microchipNumber: microchipNumber.trim(),
        registrationNumber: registrationNumber.trim(),
        veterinarianName: veterinarianName.trim(),
        veterinarianPhone: veterinarianPhone.trim(),
        veterinarianAddress: veterinarianAddress.trim(),
        emergencyVetName: emergencyVetName.trim(),
        emergencyVetPhone: emergencyVetPhone.trim(),
        medicalConditions: medicalConditions.trim(),
        medications: medications.trim(),
        allergies: allergies.trim(),
        feedingInstructions: feedingInstructions.trim(),
        specialNeeds: specialNeeds.trim(),
        behavioralNotes: behavioralNotes.trim(),
        emergencyContactName: emergencyContactName.trim(),
        emergencyContactPhone: emergencyContactPhone.trim(),
        emergencyContactRelationship: emergencyContactRelationship.trim(),
      };

      let savedPet;

      if (isEditing && petData?.id) {
        // Update existing record
        savedPet = await petInfoService.updatePetInfo(
          petData.id,
          petUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedPet?.data || savedPet;
        logger.debug('Pet updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('PetInfoLockUnlock', { petId: updatedData.id });
      } else {
        // Create new record
        savedPet = await petInfoService.createPetInfo(petUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedPet?.data || savedPet;
        logger.debug('Pet saved successfully with ID:', createdData.id);

        // Store the saved ID for the Done button
        setSavedPetId(createdData.id);

        // Clear the form and reset states for Add Another
        setPetName('');
        setPetType('');
        setBreed('');
        setAge('');
        setWeight('');
        setColor('');
        setGender('');
        setMicrochipNumber('');
        setRegistrationNumber('');
        setVeterinarianName('');
        setVeterinarianPhone('');
        setVeterinarianAddress('');
        setEmergencyVetName('');
        setEmergencyVetPhone('');
        setMedicalConditions('');
        setMedications('');
        setAllergies('');
        setFeedingInstructions('');
        setSpecialNeeds('');
        setBehavioralNotes('');
        setEmergencyContactName('');
        setEmergencyContactPhone('');
        setEmergencyContactRelationship('');

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset saving so buttons are interactive
        setSaving(false);
      }
    } catch (error) {
      logger.error('Error saving pet:', error);
      Alert.alert('Error', 'Failed to save pet information. Please try again.');
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setPetName('');
    setPetType('');
    setBreed('');
    setAge('');
    setWeight('');
    setColor('');
    setGender('');
    setMicrochipNumber('');
    setRegistrationNumber('');
    setVeterinarianName('');
    setVeterinarianPhone('');
    setVeterinarianAddress('');
    setEmergencyVetName('');
    setEmergencyVetPhone('');
    setMedicalConditions('');
    setMedications('');
    setAllergies('');
    setFeedingInstructions('');
    setSpecialNeeds('');
    setBehavioralNotes('');
    setEmergencyContactName('');
    setEmergencyContactPhone('');
    setEmergencyContactRelationship('');
    setShowAddAnotherDone(false);
    setSaving(false);
  };

  const handleDone = async () => {
    setSaving(false);
    logger.debug('[Done Debug] Using ID:', savedPetId);

    // If we don't have a saved ID, get the most recent pet entry
    if (!savedPetId) {
      try {
        const response = await petInfoService.getPetInfo(5);
        logger.debug('[Done Debug] API response:', response);

        // Handle response format - check if it's wrapped in a success/data object
        const allPets = response?.data || response;
        logger.debug('[Done Debug] Extracted pets array:', allPets);

        const mostRecent = allPets && Array.isArray(allPets) && allPets.length > 0 ? allPets[allPets.length - 1] : null;
        if (mostRecent) {
          logger.debug('[Done Debug] Using most recent ID:', mostRecent.id);
          navigation.navigate('PetInfoLockUnlock', { petId: mostRecent.id });
        } else {
          logger.debug('[Done Debug] No pet entries found in array:', allPets);
          Alert.alert('Error', 'No pet information found. Please create an entry first.');
        }
      } catch (error) {
        logger.error('[Done Debug] Error getting pets:', error);
        Alert.alert('Error', 'Failed to get pet information.');
      }
    } else {
      navigation.navigate('PetInfoLockUnlock', { petId: savedPetId });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Pet Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={true}
        >
          {/* Basic Pet Information */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Basic Information</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Pet's Name *</Text>
            <TextInput
              style={styles.input}
              value={petName}
              onChangeText={setPetName}
              placeholder="Enter pet's name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Type of Pet</Text>
            <TextInput
              style={styles.input}
              value={petType}
              onChangeText={setPetType}
              placeholder="Dog, Cat, Bird, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Breed</Text>
            <TextInput
              style={styles.input}
              value={breed}
              onChangeText={setBreed}
              placeholder="Enter breed"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Age (years)</Text>
            <TextInput
              style={styles.input}
              value={age}
              onChangeText={(text) => setAge(text.replace(/[^0-9]/g, ''))}
              placeholder="Enter age"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Weight (lbs)</Text>
            <TextInput
              style={styles.input}
              value={weight}
              onChangeText={(text) => setWeight(text.replace(/[^0-9]/g, ''))}
              placeholder="Enter weight"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Color</Text>
            <TextInput
              style={styles.input}
              value={color}
              onChangeText={setColor}
              placeholder="Enter color"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Gender</Text>
            <TextInput
              style={styles.input}
              value={gender}
              onChangeText={setGender}
              placeholder="Male, Female, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Microchip Number</Text>
            <TextInput
              style={styles.input}
              value={microchipNumber}
              onChangeText={setMicrochipNumber}
              placeholder="Enter microchip number"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Registration Number</Text>
            <TextInput
              style={styles.input}
              value={registrationNumber}
              onChangeText={setRegistrationNumber}
              placeholder="Enter registration number"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Veterinary Information */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Veterinary Information</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Veterinarian Name</Text>
            <TextInput
              style={styles.input}
              value={veterinarianName}
              onChangeText={setVeterinarianName}
              placeholder="Enter veterinarian name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Veterinarian Phone</Text>
            <TextInput
              style={styles.input}
              value={veterinarianPhone}
              onChangeText={(text) => setVeterinarianPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Veterinarian Address</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={veterinarianAddress}
              onChangeText={setVeterinarianAddress}
              placeholder="Enter veterinarian address"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Vet Name</Text>
            <TextInput
              style={styles.input}
              value={emergencyVetName}
              onChangeText={setEmergencyVetName}
              placeholder="Enter emergency vet name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Vet Phone</Text>
            <TextInput
              style={styles.input}
              value={emergencyVetPhone}
              onChangeText={(text) => setEmergencyVetPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          {/* Medical Information */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Medical Information</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medical Conditions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={medicalConditions}
              onChangeText={setMedicalConditions}
              placeholder="Enter any medical conditions"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medications</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={medications}
              onChangeText={setMedications}
              placeholder="Enter current medications"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Allergies</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={allergies}
              onChangeText={setAllergies}
              placeholder="Enter any allergies"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          {/* Care Instructions */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Care Instructions</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Feeding Instructions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={feedingInstructions}
              onChangeText={setFeedingInstructions}
              placeholder="Enter feeding schedule and instructions"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Needs</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specialNeeds}
              onChangeText={setSpecialNeeds}
              placeholder="Enter any special care requirements"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Behavioral Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={behavioralNotes}
              onChangeText={setBehavioralNotes}
              placeholder="Enter behavioral traits and preferences"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          {/* Emergency Contact */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Emergency Contact</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Contact Name</Text>
            <TextInput
              style={styles.input}
              value={emergencyContactName}
              onChangeText={setEmergencyContactName}
              placeholder="Enter emergency contact name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Contact Phone</Text>
            <TextInput
              style={styles.input}
              value={emergencyContactPhone}
              onChangeText={(text) => setEmergencyContactPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship to Pet Owner</Text>
            <TextInput
              style={styles.input}
              value={emergencyContactRelationship}
              onChangeText={setEmergencyContactRelationship}
              placeholder="Friend, Family Member, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {saving ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSave}
                >
                  <Text style={styles.submitButtonText}>
                    {isEditing ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350), // Extra padding for keyboard (iOS keyboard ~346px)
  },
  sectionHeader: {
    marginTop: ms(20),
    marginBottom: ms(15),
    paddingBottom: ms(10),
    borderBottomWidth: ms(1),
    borderBottomColor: '#29BFC0',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
});